/**
 * Convert a saved invoice/deviz into the state shape used by Calculator/DevizPage.
 *
 * Selections are keyed by `${materialId}::${color}` where color is "Mat"
 * (base price) or one of the material's defined color names.
 *
 * @param {object} inv  - The invoice document returned by GET /invoices/:id
 * @param {Array}  materials - Current materials list.
 */
const MAT = "Mat";

export function invoiceToFormState(inv, materials) {
  const matsById = new Map((materials || []).map((m) => [m.id, m]));
  const matsByName = new Map(
    (materials || []).map((m) => [(m.name || "").trim().toLowerCase(), m])
  );

  const resolveMaterial = (line) => {
    if (line?.material_id && matsById.has(line.material_id)) {
      return matsById.get(line.material_id);
    }
    const key = (line?.name || "").trim().toLowerCase();
    return key ? matsByName.get(key) || null : null;
  };

  // Map a stored line color to the catalog casing of the material's color,
  // falling back to "Mat" when empty or no longer defined.
  const resolveColor = (mat, rawColor) => {
    const c = (rawColor || "").trim();
    if (!c || c.toLowerCase() === MAT.toLowerCase()) return MAT;
    const found = (mat.colors || []).find(
      (x) => (x.name || "").trim().toLowerCase() === c.toLowerCase()
    );
    return found ? (found.name || "").trim() : c;
  };

  const selections = {};
  const usedColors = new Map(); // lowercase -> casing
  for (const ln of inv?.lines || []) {
    const qty = Number(ln.quantity || 0);
    if (qty <= 0) continue;
    const mat = resolveMaterial(ln);
    if (!mat) continue; // material was deleted from catalog — skip silently
    const color = resolveColor(mat, ln.color);
    usedColors.set(color.toLowerCase(), color);
    const key = `${mat.id}::${color}`;
    const hasWidth = Number(mat.width_m || 0) > 0;
    if (hasWidth) {
      // We don't have the original sheets breakdown, only the resulting m².
      selections[key] = {
        mode: "direct",
        direct_m2: qty,
        notes: ln.notes || "",
      };
    } else {
      selections[key] = {
        quantity: qty,
        notes: ln.notes || "",
      };
    }
  }
  const colorsList = Array.from(usedColors.values());
  return {
    selections,
    selectedColors: colorsList.length ? colorsList : [MAT],
    client: {
      lastName: inv?.client_last_name || "",
      firstName: inv?.client_first_name || "",
      cui: inv?.client_cui || "",
      cnp: inv?.client_cnp || "",
      address: inv?.client_address || "",
      phone: inv?.client_phone || "",
      regCom: inv?.client_reg_com || "",
    },
    delegateName: inv?.delegate_name || "",
    discountPct: inv?.discount_pct ? String(inv.discount_pct) : "",
    advance: inv?.advance ? String(inv.advance) : "",
    includePrices: !!inv?.include_prices,
    includeCodes: inv?.include_codes !== false,
  };
}
